import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";

const MaskedLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const TRUST = ["Fast pris før opstart", "100 % mobilvenlig", "Vi svarer normalt inden for 1 hverdag"];

export const Hero = ({ onNavigate }) => {
  const imgRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: imgRef,
    offset: ["start end", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [-48, 48]);

  const go = (href) => (e) => {
    e.preventDefault();
    onNavigate(href);
  };

  return (
    <section id="hjem" className="pt-[72px] overflow-hidden" data-testid="hero-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-24 lg:pt-32 pb-12">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-4 py-1.5 mb-8"
        >
          <span className="w-2 h-2 rounded-full bg-emerald-500" aria-hidden="true" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Webbureau for danske virksomheder
          </span>
        </motion.div>

        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-zinc-900 leading-[1.05] max-w-4xl">
          <MaskedLine delay={0.15}>En hjemmeside, der</MaskedLine>
          <MaskedLine delay={0.28}>arbejder for din virksomhed.</MaskedLine>
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.55 }}
          className="mt-7 text-lg sm:text-xl text-zinc-600 leading-relaxed max-w-2xl"
        >
          Vi designer moderne hjemmesider til danske virksomheder, der vil fremstå
          professionelle og gøre det nemmere for kunder at vælge dem. Fast pris,
          enkel proces og ingen teknisk hovedpine.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.7 }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <a
            href="#kontakt"
            onClick={go("#kontakt")}
            data-testid="hero-primary-cta"
            className="inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-medium rounded-full px-8 py-4 hover:bg-zinc-700 hover:-translate-y-0.5 transition-all duration-200"
          >
            Få et gratis forslag
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
          <a
            href="#cases"
            onClick={go("#cases")}
            data-testid="hero-secondary-cta"
            className="inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-900 font-medium rounded-full px-8 py-4 hover:border-zinc-900 hover:-translate-y-0.5 transition-all duration-200"
          >
            Se vores arbejde
          </a>
        </motion.div>

        <motion.ul
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.9 }}
          className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
        >
          {TRUST.map((item) => (
            <li key={item} className="flex items-center gap-2 text-sm text-zinc-500">
              <Check className="w-4 h-4 text-emerald-500" aria-hidden="true" />
              {item}
            </li>
          ))}
        </motion.ul>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-28">
        <motion.div
          ref={imgRef}
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative rounded-3xl overflow-hidden aspect-[16/8] sm:aspect-[16/7] bg-zinc-100"
        >
          <motion.img
            src="https://images.unsplash.com/photo-1719561975005-d2c2b08c6ed8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
            alt="Arbejdsplads hos Nordic Web Studio"
            style={{ y }}
            className="absolute inset-0 w-full h-[120%] object-cover -top-[10%]"
          />
        </motion.div>
      </div>
    </section>
  );
};
